import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import './OrgPublicProfile.css';
import { CFR_CATEGORIES } from "../../constants/cfrCategories";


const PRIORITY_COLORS = {
  critical: { bg: '#fee2e2', color: '#991b1b' },
  core: { bg: '#fef3c7', color: '#92400e' },
  supplemental: { bg: '#e0f2fe', color: '#075985' }
};

const ORG_TYPE_LABELS = {
  homeless_shelter: 'Homeless shelter',
  youth_center: 'Youth center',
  diaper_bank: 'Diaper bank',
  domestic_violence: 'Domestic violence shelter',
  transitional_housing: 'Transitional housing',
  food_pantry: 'Food pantry',
  family_services: 'Family services',
  other: 'Nonprofit organization'
};

function OrgPublicProfile() {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { userProfile } = useAuth();
  const { addToCart, removeFromCart, isInCart } = useCart();

  const [org, setOrg] = useState(null);
  const [needs, setNeeds] = useState([]);
  const [needTypeFilter, setNeedTypeFilter] = useState('all');
  const [fulfilledOrders, setFulfilledOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchData(); }, [orgId]);

  async function fetchData() {
    try {
      const [orgDoc, needsSnap] = await Promise.all([
        getDoc(doc(db, 'organizations', orgId)),
        getDocs(query(collection(db, 'needs'), where('orgId', '==', orgId), where('status', '==', 'open')))
      ]);

      if (!orgDoc.exists() || orgDoc.data().status !== 'active') {
        setOrg(null);
        setLoading(false);
        return;
      }

      setOrg({ id: orgDoc.id, ...orgDoc.data() });

      const visibleNeeds = needsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(n => n.isParent !== true);

      setNeeds(visibleNeeds);
      setFulfilledOrders([]);
    } catch (err) {
      console.error('Error fetching org:', err);
      setOrg(null);
    } finally {
      setLoading(false);
    }
  }

  const filteredNeeds = needs.filter(need => {
    if (needTypeFilter === 'all') return true;
    return (need.type || 'supply') === needTypeFilter;
  });

  function copyLink() {
    navigator.clipboard.writeText(`${window.location.origin}/o/${orgId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleTakeResponsibility(need) {
  const needType = need.type || 'supply';

  // Not signed in: donor signup
  if (!userProfile) {
    navigate(
      `/give/signup?need=${need.id}&item=${encodeURIComponent(need.itemName)}&org=${encodeURIComponent(org.orgName)}&reason=signup_required`
    );
    return;
  }

  // Signed in as org/admin: don't send them into donor adoption flow
  if (userProfile.role === 'org' || userProfile.role === 'organization' || userProfile.role === 'admin') {
    alert('You are signed in as an organization/admin. To sponsor this need, sign in with a donor account.');
    return;
  }

  // Signed in as donor + sponsorship
  if (needType === 'sponsorship') {
  navigate(`/give/sponsorships/${need.id}/commit`);
  return;
}

  // Signed in as donor + supply
  if (isInCart(need.id)) {
    removeFromCart(need.id);
  } else {
    addToCart({ ...need, orgName: org.orgName, orgId: org.id });
  }
}

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif', color: '#888' }}>
      Loading...
    </div>
  );

  if (!org) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif', textAlign: 'center', padding: '2rem' }}>
      <div>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔍</div>
        <h2>Organization not found</h2>
        <p style={{ color: '#888', marginBottom: '1.5rem' }}>This organization may not be active on somebody.gives yet.</p>
        <button onClick={() => navigate('/')} style={{ background: '#2d6a4f', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
          Back to somebody.gives
        </button>
      </div>
    </div>
  );

  function fmtDate(ts) {
    if (!ts) return 'recently';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const orgCfrCategories = (org.cfrCategories || [])
    .map(val => CFR_CATEGORIES.find(c => c.value === val))
    .filter(Boolean);

  return (
    <div className="org-public-page">

      <header className="org-public-header">
        <div className="org-public-logo" onClick={() => navigate('/')}>somebody.gives</div>
        <div className="org-public-nav">
          <button onClick={() => navigate('/give/orgs')} style={{ background: 'transparent', color: '#2d6a4f', border: '2px solid #2d6a4f', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Browse orgs</button>
          <button onClick={() => navigate('/give/browse-public')} style={{ background: '#2d6a4f', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Browse needs</button>
        </div>
      </header>

      <main className="org-public-main">

        <div className="org-profile-card">
          <div className="org-profile-header">

            <div className="org-profile-logo">
              {org.logoUrl ? (
                <img src={org.logoUrl} alt={`${org.orgName} logo`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <div style={{ fontWeight: 800, fontSize: '1.5rem', color: '#2d6a4f' }}>{(org.orgName || 'O').charAt(0)}</div>
              )}
            </div>

            <div className="org-profile-info">
              <div className="org-profile-meta">
                {ORG_TYPE_LABELS[org.orgType] || 'Nonprofit organization'} • Springfield, MO
              </div>

              <h1 className="org-profile-title">{org.orgName}</h1>

              {org.missionStatement && (
                <p style={{ color: '#444', marginTop: '0.5rem', lineHeight: 1.55 }}>{org.missionStatement}</p>
              )}

              {org.populationsServed && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#555' }}>
                  <strong>Serves:</strong> {org.populationsServed}
                </div>
              )}

              {orgCfrCategories.length > 0 && (
                <div style={{ marginTop: '0.875rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
                    Community Priority (CFR)
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {orgCfrCategories.map(cat => (
                      <span key={cat.value} style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, background: '#e0f2fe', color: '#075985' }}>
                        {cat.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {org.website && (
                <div style={{ marginTop: '0.75rem' }}>
                  <a href={org.website} target="_blank" rel="noreferrer" style={{ color: '#2d6a4f', fontWeight: 700, fontSize: '0.9rem' }}>Visit website →</a>
                </div>
              )}
            </div>

            <button onClick={copyLink} className={`org-share-button ${copied ? 'copied' : ''}`}>
              {copied ? '✓ Copied' : 'Share'}
            </button>

          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.35rem' }}>Open needs from {org.orgName}</h2>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>Select a supply need or sponsorship opportunity below.</p>
        </div>

        {needs.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {['all', 'supply', 'sponsorship'].map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setNeedTypeFilter(type)}
                style={{
                  padding: '0.45rem 0.9rem',
                  borderRadius: '999px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 700,
                  background: needTypeFilter === type ? '#2d6a4f' : '#e5e7eb',
                  color: needTypeFilter === type ? 'white' : '#111827'
                }}
              >
                {type === 'all'
                  ? 'All'
                  : type === 'supply'
                    ? 'Supply Needs'
                    : 'Sponsorships'}
              </button>
            ))}
          </div>
        )}

        {needs.length === 0 && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '3rem', textAlign: 'center', border: '1px solid #eee', color: '#888' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🎉</div>
            <div style={{ fontWeight: 700, color: '#333', marginBottom: '0.5rem' }}>All needs are currently funded</div>
            <button onClick={() => navigate('/give/orgs')} style={{ marginTop: '1.25rem', background: '#2d6a4f', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}>
              Browse other organizations
            </button>
          </div>
        )}

        {needs.length > 0 && filteredNeeds.length === 0 && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '3rem', textAlign: 'center', border: '1px solid #eee', color: '#888' }}>
            <div style={{ fontWeight: 700, color: '#333', marginBottom: '0.5rem' }}>No needs match this filter</div>
            <button onClick={() => setNeedTypeFilter('all')} style={{ marginTop: '1.25rem', background: '#2d6a4f', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}>
              Show all
            </button>
          </div>
        )}

        {filteredNeeds.map(need => {
          const needType = need.type || 'supply';
          const isSponsorship = needType === 'sponsorship';

          const needCfr = (need.cfrCategories || [])
            .map(val => CFR_CATEGORIES.find(c => c.value === val))
            .filter(Boolean);

          return (
            <div key={need.id} className="org-need-card">
              <div className="org-need-left">
                <div className="org-need-logo">
                  {org.logoUrl ? (
                    <img src={org.logoUrl} alt={`${org.orgName} logo`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <div style={{ color: '#2d6a4f', fontWeight: 800 }}>{(org.orgName || 'O').charAt(0)}</div>
                  )}
                </div>

                <div className="org-need-text">
                  <div className="org-need-badges">
                    <span style={{
                      background: isSponsorship ? '#f3e8ff' : '#e0f2fe',
                      color: isSponsorship ? '#6b21a8' : '#075985',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '20px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}>
                      {needType}
                    </span>

                    <span style={{ background: PRIORITY_COLORS[need.priority]?.bg, color: PRIORITY_COLORS[need.priority]?.color, padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      {need.priority}
                    </span>

                    <span style={{ fontSize: '0.8rem', color: '#888' }}>{need.frequency}</span>
                  </div>

                  <div className="org-need-title">{need.itemName}</div>
                  <div className="org-need-impact">{need.impactStatement || need.description}</div>

                  {needCfr.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                      {needCfr.map(cat => (
                        <span key={cat.value} style={{ padding: '0.15rem 0.6rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>
                          {cat.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="org-need-action">
                {isSponsorship ? (
                  <div className="org-need-price">
                    Sponsorship
                    <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: 400, color: '#888' }}>matchmaking</span>
                  </div>
                ) : (
                  <div className="org-need-price">
                    ${Math.ceil(need.pricePerCycle || 0)}
                    <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#888' }}>/{need.frequency}</span>
                  </div>
                )}

                <button onClick={() => handleTakeResponsibility(need)} className={`org-need-button ${!isSponsorship && isInCart(need.id) ? 'in-cart' : ''}`}>
                  {isSponsorship
                    ? 'Sponsor this'
                    : isInCart(need.id)
                      ? '✓ In cart'
                      : 'Take responsibility'}
                </button>

                <div className="org-need-note">
                  {userProfile
                    ? isSponsorship
                      ? 'Org will contact you'
                      : 'Added to your cart'
                    : 'Signup required'}
                </div>
              </div>
            </div>
          );
        })}

        {fulfilledOrders.length > 0 && (
          <div className="org-fulfilled-section">
            <div className="org-needs-intro">
              <h2>Recent deliveries to {org.orgName}</h2>
              <p>These needs were fulfilled through somebody.gives donors.</p>
            </div>
            {fulfilledOrders.map(order => (
              <div key={order.id} className="org-fulfilled-card">
                <div className="org-fulfilled-left">
                  <div className="org-fulfilled-badge">received</div>
                  <div className="org-fulfilled-title">{order.itemName}</div>
                  <div className="org-fulfilled-recognition">{order.donorRecognition?.displayText || 'A local donor'} helped provide this need.</div>
                  {order.impactStatement && <div className="org-fulfilled-impact">Impact: {order.impactStatement}</div>}
                </div>
                <div className="org-fulfilled-right">
                  <div className="org-fulfilled-date">Received {fmtDate(order.receivedAt)}</div>
                  <div className="org-fulfilled-meta">Qty: {order.quantityPerCycle || 1}{order.frequency ? ` · ${order.frequency}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}

      </main>
    </div>
  );
}

export default OrgPublicProfile;